<template>
	<div class="chat-input-area">
		<div :class="['edit-chat-container', isEmpty ? '' : 'not-empty']" contenteditable="true"
			@paste.prevent="onPaste" @keydown="onKeydown" @compositionstart="compositionFlag = true"
			@compositionend="onCompositionEnd" @input="onEditorInput" @mousedown="onMousedown" ref="content"
			@blur="onBlur" :data-placeholder="placeholderText">
		</div>
		<chat-at-box @select="onAtSelect" :search-text="atSearchText" ref="atBox" :group="group"
			:members="groupMembers"></chat-at-box>
	</div>
</template>

<script>
import { consumeClipboardMediaFilesCache, getUserKey } from '@/utils/common';
import ChatAtBox from "./ChatAtBox";
export default {
	name: "ChatInput",
	components: { ChatAtBox },
	props: {
		group: {
			type: Object,
		},
		groupMembers: {
			type: Array,
		},
		chat: {
			type: Object
		},
		showFileConfirmDialog: {
			type: Boolean,
			default: false,
		}
	},
	data() {
		return {
			imageList: [],
			fileList: [],
			currentId: 0,
			atSearchText: null,
			compositionFlag: false,
			atIng: false,
			isEmpty: true,
			changeStored: true,
			blurRange: null,
			undoStack: [],
			redoStack: [],
			lastContent: '',
			lastSendContent: '', // 上次发送内容
			updateTimer: null, // 防抖定时器
			sendMode: 'enter_send',
		}
	},
	mounted() {
		this.initSendMode();
		this.$eventBus && this.$eventBus.$on('chat:send-mode-change', this.onSendModeChange);
		this.$eventBus && this.$eventBus.$on('chat:at-member', this.insertAtUser);
		this.$nextTick(() => this.restoreDraft(this.chat));
	},
	beforeDestroy() {
		this.flushDraft(this.chat);
		this.$eventBus && this.$eventBus.$off('chat:send-mode-change', this.onSendModeChange);
		this.$eventBus && this.$eventBus.$off('chat:at-member', this.insertAtUser);
	},
	watch: {
		chat: {
			handler(newChat, oldChat) {
				if (!newChat) {
					return;
				}
				if (oldChat && newChat.type === oldChat.type && String(newChat.targetId) === String(oldChat.targetId)) {
					return;
				}
				if (oldChat) {
					this.flushDraft(oldChat);
				}
				this.$nextTick(() => this.restoreDraft(newChat));
			}
		}
	},
	methods: {
		initSendMode() {
			const mineId = this.$store?.state?.userStore?.userInfo?.id;
			if (!mineId) {
				this.sendMode = 'enter_send';
				return;
			}
			this.sendMode = localStorage.getItem(getUserKey('sendMode', mineId)) || 'enter_send';
		},
		onSendModeChange(mode) {
			if (!mode) return;
			this.sendMode = mode;
		},
		isEditorVisualEmpty(html) {
			const plain = (html || '')
				.replace(/<[^>]*>/g, '')
				.replace(/&nbsp;/g, '')
				.replace(/[\u00A0\u200B\uFEFF]/g, '')
				.trim();
			return plain === '';
		},
		normalizeSendText(text) {
			return (text || '')
				.replace(/&nbsp;/g, ' ')
				.replace(/\u00A0/g, ' ')
				.replace(/[\u200B\uFEFF]/g, '')
				.replace(/ {2,}/g, ' ')
				.trim();
		},
		atUserSendText(node) {
			const mention = String(node.innerText || node.textContent || '')
				.replace(/&nbsp;/g, ' ')
				.replace(/[\u00A0\u200B\uFEFF]/g, '')
				.replace(/\s+$/g, '');
			return mention ? mention + ' ' : '';
		},
		pushUndo() {
			const content = this.$refs.content.innerHTML;
			if (content !== this.lastContent) {
				if (this.undoStack.length > 50) this.undoStack.shift(); // 限制栈大小
				this.undoStack.push(this.lastContent);
				this.lastContent = content;
				this.redoStack = [];
				this.$emit('undo-redo-state', {
					canUndo: this.undoStack.length > 0,
					canRedo: this.redoStack.length > 0
				});
			}
		},
		undo() {
			if (!this.undoStack.length) return;
			this.redoStack.push(this.$refs.content.innerHTML);
			const prev = this.undoStack.pop();
			this.$refs.content.innerHTML = prev || '';
			this.lastContent = prev || '';
			this.updateChatStore();
			this.selectElement(this.$refs.content, 1);
			this.$emit('undo-redo-state', {
				canUndo: this.undoStack.length > 0,
				canRedo: this.redoStack.length > 0
			});
			// 等待dom更新
			setTimeout(() => {
				let s = this.$refs.content.innerHTML.trim();
				// 空dom时，需要刷新dom
				if (this.isEditorVisualEmpty(s)) {
					// 拼接随机长度的空格，以刷新dom
					this.empty();
					this.isEmpty = true;
					this.selectElement(this.$refs.content);
				} else {
					this.isEmpty = false;
				}
			})
		},
		redo() {
			if (!this.redoStack.length) return;
			this.undoStack.push(this.$refs.content.innerHTML);
			const next = this.redoStack.pop();
			this.$refs.content.innerHTML = next || '';
			this.lastContent = next || '';
			this.updateChatStore();
			this.selectElement(this.$refs.content, 1);
			this.$emit('undo-redo-state', {
				canUndo: this.undoStack.length > 0,
				canRedo: this.redoStack.length > 0
			});
			// 等待dom更新
			setTimeout(() => {
				let s = this.$refs.content.innerHTML.trim();
				// 空dom时，需要刷新dom
				if (this.isEditorVisualEmpty(s)) {
					// 拼接随机长度的空格，以刷新dom
					this.empty();
					this.isEmpty = true;
					this.selectElement(this.$refs.content);
				} else {
					this.isEmpty = false;
				}
			})
		},
		collectPastedFiles(e) {
			const files = [];
			const items = (e.clipboardData || window.clipboardData)?.items;
			if (!items || !items.length) {
				return files;
			}
			for (let i = 0; i < items.length; i++) {
				try {
					const item = items[i];
					if (item.kind !== 'file') {
						continue;
					}
					let file = item.getAsFile();
					if (!file || file.size < 0) {
						continue;
					}
					if (!file.name) {
						let defaultName = 'file';
						if (file.type && file.type.indexOf('image/') === 0) {
							defaultName = 'image.png';
						} else if (file.type && file.type.indexOf('video/') === 0) {
							defaultName = 'video.mp4';
						}
						file = new File([file], defaultName, { type: file.type || 'application/octet-stream' });
					}
					files.push(file);
				} catch (fileError) {
					console.warn('Error processing clipboard file item:', fileError);
				}
			}
			return files;
		},
		onPaste(e) {
			try {
				this.pushUndo();

				// 检查是否有有效的选择范围
				let selection = window.getSelection();
				if (!selection.rangeCount) {
					console.warn('No valid selection range found');
					return;
				}

				let range = selection.getRangeAt(0);
				if (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset) {
					range.deleteContents();
				}

				// 优先处理剪贴板中的文件（含从聊天消息复制的图片/视频/文件）
				const pastedFiles = this.collectPastedFiles(e);
				if (pastedFiles.length > 0) {
					this.$emit('paste-files', pastedFiles);
					return;
				}

				// 应用内复制媒体的粘贴缓存（剪贴板写入失败时仍可粘贴）
				const cachedFiles = consumeClipboardMediaFilesCache();
				if (cachedFiles && cachedFiles.length > 0) {
					this.$emit('paste-files', cachedFiles);
					return;
				}

				// 优先处理HTML格式内容
				let htmlContent = e.clipboardData.getData('text/html');
				let plainText = e.clipboardData.getData('Text');

				// 处理富文本内容
				// if (htmlContent && htmlContent.trim()) {
				// 	this.pasteHtmlContent(htmlContent, range);
				// } else if (plainText && plainText.trim()) {
				// 	this.pastePlainText(plainText, range);
				// }
				this.pastePlainText(plainText, range);

				range.collapse();
				this.updateChatStore();
				this.isEmpty = false;
			} catch (error) {
				console.error('Error in onPaste:', error);
				// 降级处理：如果富文本处理失败，尝试纯文本粘贴
				try {
					let plainText = e.clipboardData.getData('Text');
					if (plainText && plainText.trim()) {
						let selection = window.getSelection();
						if (selection.rangeCount) {
							let range = selection.getRangeAt(0);
							this.pastePlainText(plainText, range);
							this.updateChatStore();
							this.isEmpty = false;
						}
					}
				} catch (fallbackError) {
					console.error('Fallback paste also failed:', fallbackError);
				}
			}
		},

		// 处理HTML内容
		pasteHtmlContent(htmlContent, range) {
			try {
				// 清理HTML内容，移除不安全的标签和属性
				let cleanHtml = this.cleanHtmlContent(htmlContent);

				// 创建临时容器来解析HTML
				let tempDiv = document.createElement('div');
				tempDiv.innerHTML = cleanHtml;

				// 处理HTML节点
				this.processHtmlNodes(tempDiv, range);
			} catch (error) {
				console.error('Error in pasteHtmlContent:', error);
				// 降级为纯文本处理
				let plainText = htmlContent.replace(/<[^>]*>/g, '').trim();
				this.pastePlainText(plainText, range);
			}
		},

		// 处理纯文本内容
		pastePlainText(plainText, range) {
			let lines = String(plainText == null ? '' : plainText).split('\n');

			for (let i = 0; i < lines.length; i++) {
				let line = lines[i];
				if (line.trim()) {
					this.insertTextWithFlags(line, range);
				}

				if (i < lines.length - 1) {
					let brElement = document.createElement('br');
					range.insertNode(brElement);
					range.setStartAfter(brElement);
					range.collapse(true);
				}
			}
		},
		insertTextWithFlags(text, range) {
			if (!this.$emo || !this.$emo.splitByFlagEmoji) {
				this.insertNodeAfterRange(document.createTextNode(text), range);
				return;
			}
			const parts = this.$emo.splitByFlagEmoji(text);
			if (!parts.length) {
				if (text) {
					this.insertNodeAfterRange(document.createTextNode(text), range);
				}
				return;
			}
			parts.forEach((part) => {
				if (part.type === 'flag' && this.$emo.createFlagElement) {
					this.insertNodeAfterRange(this.$emo.createFlagElement(part.value), range);
				} else if (part.value) {
					this.insertNodeAfterRange(document.createTextNode(part.value), range);
				}
			});
		},
		insertNodeAfterRange(node, range) {
			range.insertNode(node);
			range.setStartAfter(node);
			range.collapse(true);
		},

		// 清理HTML内容
		cleanHtmlContent(html) {
			// 移除script和style标签
			html = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
			html = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

			// 移除事件处理器
			html = html.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');

			// 移除javascript协议
			html = html.replace(/javascript:/gi, '');

			// 移除不安全的属性
			html = html.replace(/\s*(href|src)\s*=\s*["']javascript:[^"']*["']/gi, '');

			return html;
		},

		// 处理HTML节点
		processHtmlNodes(container, range) {
			if (!container || !range) {
				console.warn('Invalid container or range in processHtmlNodes');
				return;
			}

			let childNodes = container.childNodes;
			if (!childNodes || !childNodes.length) {
				return;
			}

			for (let i = 0; i < childNodes.length; i++) {
				let node = childNodes[i];
				if (!node) continue;

				if (node.nodeType === Node.TEXT_NODE) {
					this.insertTextWithFlags(node.textContent || '', range);
				} else if (node.nodeType === Node.ELEMENT_NODE) {
					// 元素节点
					this.processElementNode(node, range);
				}

			}

		},

		// 处理元素节点
		processElementNode(element, range) {
			if (!element || !range) {
				console.warn('Invalid element or range in processElementNode');
				return;
			}

			let tagName = element.tagName ? element.tagName.toLowerCase() : '';
			if (!tagName) {
				console.warn('Invalid tagName in element');
				return;
			}

			// 处理块级元素
			if (this.isBlockElement(tagName)) {
				// 创建新的div元素
				let divElement = document.createElement('div');
				divElement.className = `pasted-${tagName}`;

				// 处理子节点
				this.processHtmlNodes(element, divElement);

				// 插入到编辑区域
				range.insertNode(divElement);
				range.collapse();
			} else if (tagName === 'br') {
				// 换行元素
				let brElement = document.createElement('br');
				range.insertNode(brElement);
				range.collapse();
			} else {
				// 内联元素
				let spanElement = document.createElement('span');
				spanElement.className = `pasted-${tagName}`;

				// 复制样式
				if (element.style && element.style.cssText) {
					spanElement.style.cssText = this.sanitizeStyles(element.style.cssText);
				}

				// 处理子节点
				this.processHtmlNodes(element, spanElement);

				// 插入到编辑区域
				range.insertNode(spanElement);
				range.collapse();
			}

		},

		// 判断是否为块级元素
		isBlockElement(tagName) {
			const blockElements = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'ul', 'ol', 'li'];
			return blockElements.includes(tagName);
		},

		// 判断是否为内联元素
		isInlineElement(tagName) {
			const inlineElements = ['span', 'strong', 'em', 'u', 's', 'code', 'a'];
			return inlineElements.includes(tagName);
		},

		// 清理样式
		sanitizeStyles(cssText) {
			try {
				if (!cssText || typeof cssText !== 'string') {
					return '';
				}

				// 允许的CSS属性
				const allowedProperties = [
					'color', 'background-color', 'font-size', 'font-weight', 'font-style',
					'text-decoration', 'text-align', 'margin', 'padding', 'border',
					'border-radius', 'line-height', 'letter-spacing'
				];

				let cleanStyles = '';
				let styles = cssText.split(';');

				for (let style of styles) {
					try {
						if (!style || !style.includes(':')) continue;

						let parts = style.split(':');
						if (parts.length < 2) continue;

						let property = parts[0].trim();
						let value = parts.slice(1).join(':').trim();

						if (property && value && allowedProperties.includes(property)) {
							// 简单的值验证
							if (value.length < 1000) { // 防止过长的值
								cleanStyles += `${property}: ${value}; `;
							}
						}
					} catch (styleError) {
						console.warn('Error processing style:', styleError);
						continue;
					}
				}

				return cleanStyles;
			} catch (error) {
				console.error('Error in sanitizeStyles:', error);
				return '';
			}
		},
		selectElement(element, endOffset) {
			let selection = window.getSelection();
			this.$nextTick(() => {
				if (!element) return;
				let t1 = document.createRange();
				// 文本节点
				if (element.nodeType === 3) {
					let maxOffset = element.textContent ? element.textContent.length : 0;
					let safeOffset = Math.min(endOffset || 0, maxOffset);
					t1.setStart(element, safeOffset);
					t1.setEnd(element, safeOffset);
				} else {
					// 元素节点
					let maxOffset = element.childNodes ? element.childNodes.length : 0;
					let safeOffset = Math.min(endOffset || 0, maxOffset);
					t1.setStart(element, safeOffset);
					t1.setEnd(element, safeOffset);
				}
				t1.collapse();
				selection.removeAllRanges();
				selection.addRange(t1);
				if (typeof element.focus === 'function') {
					element.focus();
				}
			})
		},
		onCompositionEnd(e) {
			this.compositionFlag = false;
			this.onEditorInput(e);
		},
		onKeydown(e) {
			const key = (e.key || '').toLowerCase();
			const modKeyPressed = e.ctrlKey || e.metaKey;
			const isModEnterSend = this.sendMode === 'mod_enter_send';
			// 撤销 Ctrl/Cmd + Z
			if (modKeyPressed && !e.shiftKey && key === 'z') {
				e.preventDefault();
				this.undo();
				return;
			}
			// 恢复 Ctrl/Cmd + Y 或 Ctrl/Cmd + Shift + Z
			if ((modKeyPressed && key === 'y') || (modKeyPressed && e.shiftKey && key === 'z')) {
				e.preventDefault();
				this.redo();
				return;
			}
			if (e.keyCode === 13) {
				e.preventDefault();
				e.stopPropagation();
				// 如果有弹窗，那么就调用父组件的确认方法直接发送文件
				if (this.showFileConfirmDialog) {
					this.$emit('confirm-files', {});
					return;
				}
				if (this.atIng) {
					console.log('选中at的人')
					this.$refs.atBox.select();
					return;
				}
				const shouldSend = isModEnterSend ? modKeyPressed : !modKeyPressed;
				if (!shouldSend) {
					let line = this.newLine();
					let after = document.createTextNode('\u00A0');
					this.isEmpty = false;
					line.appendChild(after);
					this.selectElement(line.childNodes[0], 0);
				} else {
					// 中文输入标记
					if (this.compositionFlag) {
						return;
					}
					this.submit();
				}
				return;
			}
			// 删除键：先删光标前的 @用户，避免 contenteditable=false 删不掉
			if (e.keyCode === 8 || e.keyCode === 46) {
				if (this.deleteAdjacentAtUser(e)) {
					return;
				}
				setTimeout(() => {
					let s = this.$refs.content.innerHTML.trim();
					if (this.isEditorVisualEmpty(s)) {
						this.empty();
						this.isEmpty = true;
						this.selectElement(this.$refs.content);
					} else {
						this.isEmpty = false;
					}
				})
			}
			// at框打开时，上下键移动特殊处理
			if (this.atIng) {
				if (e.keyCode === 38) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveUp();
				}
				if (e.keyCode === 40) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveDown();
				}
			}

		},
		onAtSelect(member) {
			this.atIng = false;
			// 选中输入的 @xx 符
			let blurRange = this.blurRange;
			let endContainer = blurRange.endContainer
			let startOffset = endContainer.data ? endContainer.data.indexOf("@" + this.atSearchText) : -1;
			let endOffset = startOffset + this.atSearchText.length + 1;
			blurRange.setStart(blurRange.endContainer, startOffset);
			blurRange.setEnd(blurRange.endContainer, endOffset);
			blurRange.deleteContents()
			blurRange.collapse();
			console.log("onAtSelect")
			this.focus();
			// 创建元素节点
			let element = document.createElement('SPAN')
			element.className = "chat-at-user";
			element.dataset.id = member.userId;
			element.contentEditable = 'false'
			// element.innerText = `@${member.showNickName}`
			element.innerText = `@${member.remarkNickName || member.userNickName || member.showNickName}`
			blurRange.insertNode(element)
			// 光标移动到末尾
			blurRange.collapse()

			// 插入空格
			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode);

			blurRange.collapse()
			this.atSearchText = "";
			this.selectElement(textNode, 1);
			this.updateChatStore();
		},
		onEditorInput(e) {
			this.isEmpty = false;
			this.changeStored = false;
			// mac 输入法在 composition 阶段对 contenteditable 很敏感，
			// 这里避免在组合输入过程中触发 store 持久化，统一在 compositionend 后处理
			if (this.compositionFlag) {
				return;
			}
			this.pushUndo();
			this.updateChatStore();
			if (this.$props.groupMembers && !this.compositionFlag) {
				let selection = window.getSelection()
				let range = selection.getRangeAt(0);
				// 截取@后面的名称作为过滤条件，并以空格结束
				let endContainer = range.endContainer;
				let endOffset = range.endOffset;
				let textContent = endContainer.textContent;
				let startIndex = -1;
				for (let i = endOffset; i >= 0; i--) {
					if (textContent[i] === '@') {
						startIndex = i;
						break;
					}
				}
				// 没有at符号，则关闭弹窗
				if (startIndex === -1) {
					this.$refs.atBox.close();
					return;
				}

				let endIndex = endOffset;
				for (let i = endOffset; i < textContent.length; i++) {
					if (textContent[i] === ' ') {
						endIndex = i;
						break;
					}
				}
				this.atSearchText = textContent.substring(startIndex + 1, endIndex).trim();
				// 打开选择弹窗
				if (this.atSearchText == '' && this.chat.type == 'GROUP') {
					this.showAtBox(e)
				}
			}
		},
		onBlur() {
			if (!this.atIng) {
				this.updateRange();
				// 失焦时立即保存
				this.updateChatStore('immediate');
			}
		},
		onMousedown() {
			if (this.atIng) {
				this.$refs.atBox.close();
				this.atIng = false;
			}
		},
		insertQuickReply(text) {
			if (!text || !String(text).trim()) {
				return;
			}
			this.pushUndo();
			this.focus();
			let selection = window.getSelection();
			if (!selection || !selection.rangeCount) {
				this.selectElement(this.$refs.content, this.$refs.content.childNodes.length);
				selection = window.getSelection();
			}
			let range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;
			if (!range || !this.$refs.content.contains(range.startContainer)) {
				this.selectElement(this.$refs.content, this.$refs.content.childNodes.length);
				selection = window.getSelection();
				range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;
			}
			if (!range) {
				return;
			}
			if (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset) {
				range.deleteContents();
			}
			this.pastePlainText(String(text), range);
			range.collapse();
			this.blurRange = range;
			this.isEmpty = false;
			this.updateChatStore();
			this.updateRange();
		},
		insertAtUser(payload) {
			if (!payload || payload.userId == null || !this.$refs.content) {
				return;
			}
			if (!this.chat || this.chat.type !== 'GROUP') {
				return;
			}
			const mineId = this.$store.state.userStore.userInfo && this.$store.state.userStore.userInfo.id;
			if (payload.userId == mineId) {
				return;
			}
			const member = (this.groupMembers || []).find((m) => m.userId == payload.userId) || {};
			const nick = member.remarkNickName || member.userNickName || member.showNickName || payload.name || '';
			if (!nick) {
				return;
			}
			this.pushUndo();
			this.focus();
			let range = null;
			try {
				if (this.blurRange && this.$refs.content.contains(this.blurRange.startContainer)) {
					range = this.blurRange;
				}
			} catch (e) {
				range = null;
			}
			if (!range) {
				range = document.createRange();
				range.selectNodeContents(this.$refs.content);
				range.collapse(false);
			}
			const selection = window.getSelection();
			try {
				selection.removeAllRanges();
				selection.addRange(range);
			} catch (e) {
				range = document.createRange();
				range.selectNodeContents(this.$refs.content);
				range.collapse(false);
			}
			if (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset) {
				range.deleteContents();
			}
			const element = document.createElement('SPAN');
			element.className = 'chat-at-user';
			element.dataset.id = payload.userId;
			element.contentEditable = 'false';
			element.innerText = `@${nick}`;
			range.insertNode(element);
			range.collapse(false);
			const textNode = document.createTextNode('\u00A0');
			range.insertNode(textNode);
			range.setStart(textNode, 1);
			range.setEnd(textNode, 1);
			range.collapse(false);
			this.blurRange = range;
			this.selectElement(textNode, 1);
			this.isEmpty = false;
			this.updateChatStore();
		},
		insertEmoji(emojiText) {
			const isFlag = this.$emo.isFlagEmoji && this.$emo.isFlagEmoji(emojiText);
			let emojiElement = isFlag && this.$emo.createFlagElement
				? this.$emo.createFlagElement(emojiText)
				: document.createElement('a');
			if (!isFlag) {
				emojiElement.innerHTML = emojiText;
			}
			let blurRange = this.blurRange;
			if (!blurRange) {
				this.focus();
				this.updateRange();
				blurRange = this.blurRange;
			}
			if (blurRange.startContainer !== blurRange.endContainer || blurRange.startOffset !== blurRange.endOffset) {
				blurRange.deleteContents();
			}
			blurRange.insertNode(emojiElement);
			blurRange.collapse();

			// 在表情后面插入一个空格，并将光标移动到空格后面
			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode);
			blurRange.collapse();

			this.selectElement(textNode, 0);
			this.updateRange();
			this.isEmpty = false;
			this.updateChatStore();
		},
		generateId() {
			return this.currentId++;
		},
		createFile(filePush) {
			let file = filePush.file;
			let fileId = filePush.fileId;
			let container = document.createElement('div');
			container.className = 'chat-file-container no-text';
			container.contentEditable = 'false';
			container.dataset.fileId = fileId;

			let left = document.createElement('div');
			left.className = 'file-position-left';
			container.appendChild(left);

			let icon = document.createElement('div');
			icon.className = 'el-icon-document';
			left.appendChild(icon);

			let right = document.createElement('div');
			right.className = 'file-position-right';
			container.appendChild(right);

			let fileName = document.createElement('div');
			fileName.className = 'file-name';
			fileName.innerText = file.name;

			let fileSize = document.createElement('div');
			fileSize.className = 'file-size';
			fileSize.innerText = this.sizeConvert(file.size);

			right.appendChild(fileName);
			right.appendChild(fileSize);

			return container;
		},
		sizeConvert(len) {
			if (len < 1024) {
				return len + 'B';
			} else if (len < 1024 * 1024) {
				return (len / 1024).toFixed(2) + 'KB';
			} else if (len < 1024 * 1024 * 1024) {
				return (len / 1024 / 1024).toFixed(2) + 'MB';
			} else {
				return (len / 1024 / 1024 / 1024).toFixed(2) + 'GB';
			}
		},
		updateRange() {
			let selection = window.getSelection();
			this.blurRange = selection.getRangeAt(0);
		},
		newLine() {
			let selection = window.getSelection();
			let range = selection.getRangeAt(0);
			let divElement = document.createElement('div');
			let endContainer = range.endContainer;
			let parentElement = endContainer.parentElement;
			if (parentElement.parentElement === this.$refs.content) {
				divElement.innerHTML = endContainer.textContent.substring(range.endOffset).trim();
				endContainer.textContent = endContainer.textContent.substring(0, range.endOffset);
				// 插入到当前div（当前行）后面
				parentElement.insertAdjacentElement('afterend', divElement);
			} else {
				divElement.innerHTML = '';
				this.$refs.content.append(divElement);
			}
			return divElement;
		},
		isAtUserNode(node) {
			return !!(node && node.nodeType === 1 && node.classList && node.classList.contains('chat-at-user'));
		},
		isBlankTextNode(node) {
			if (!node || node.nodeType !== 3) {
				return false;
			}
			return !String(node.textContent || '').replace(/[\u00A0\u200B\uFEFF\s]/g, '');
		},
		skipBlank(node, dir) {
			let current = node;
			while (this.isBlankTextNode(current)) {
				current = dir < 0 ? current.previousSibling : current.nextSibling;
			}
			return current;
		},
		deleteAdjacentAtUser(e) {
			const selection = window.getSelection();
			if (!selection || !selection.rangeCount || !selection.isCollapsed) {
				return false;
			}
			const range = selection.getRangeAt(0);
			const container = range.startContainer;
			const offset = range.startOffset;
			const backspace = e.keyCode === 8;
			let target = null;
			if (container.nodeType === 3) {
				const text = container.textContent || '';
				if (backspace) {
					const before = text.slice(0, offset);
					if (!before.replace(/[\u00A0\u200B\uFEFF\s]/g, '')) {
						target = this.skipBlank(container.previousSibling, -1);
						if (!target && container.parentNode && container.parentNode !== this.$refs.content) {
							target = this.skipBlank(container.parentNode.previousSibling, -1);
						}
					}
				} else {
					const after = text.slice(offset);
					if (!after.replace(/[\u00A0\u200B\uFEFF\s]/g, '')) {
						target = this.skipBlank(container.nextSibling, 1);
					}
				}
			} else if (container.nodeType === 1) {
				const children = container.childNodes;
				if (backspace && offset > 0) {
					target = this.skipBlank(children[offset - 1], -1);
				} else if (!backspace && offset < children.length) {
					target = this.skipBlank(children[offset], 1);
				}
			}
			if (!this.isAtUserNode(target)) {
				return false;
			}
			e.preventDefault();
			e.stopPropagation();
			this.pushUndo();
			const parent = target.parentNode;
			parent.removeChild(target);
			this.isEmpty = this.isEditorVisualEmpty(this.$refs.content.innerHTML);
			this.updateChatStore('immediate');
			this.$nextTick(() => {
				if (this.isEmpty) {
					this.empty();
					this.selectElement(this.$refs.content);
					return;
				}
				try {
					const next = window.getSelection();
					const caret = document.createRange();
					caret.selectNodeContents(parent);
					caret.collapse(backspace);
					next.removeAllRanges();
					next.addRange(caret);
				} catch (err) {
					this.selectElement(this.$refs.content, 1);
				}
			});
			return true;
		},
		prepareAtUserSpans(root) {
			if (!root || !root.querySelectorAll) {
				return;
			}
			const spans = root.querySelectorAll('.chat-at-user');
			for (let i = 0; i < spans.length; i++) {
				spans[i].contentEditable = 'false';
			}
		},
		flushDraft(chat) {
			if (this.updateTimer) {
				clearTimeout(this.updateTimer);
				this.updateTimer = null;
			}
			this._saveDraft(null, chat);
		},
		restoreDraft(chat) {
			const draftChat = chat || this.chat;
			const html = draftChat && draftChat.draftContent;
			if (!this.$refs.content) {
				return;
			}
			this.undoStack = [];
			this.redoStack = [];
			this.lastContent = '';
			if (html && !this.isEditorVisualEmpty(html)) {
				this.$refs.content.innerHTML = html;
				this.prepareAtUserSpans(this.$refs.content);
				if (this.$emo.replaceFlagsInElement) {
					this.$emo.replaceFlagsInElement(this.$refs.content);
				}
				this.isEmpty = false;
				this.lastContent = this.$refs.content.innerHTML;
				this.selectElement(this.$refs.content, 1);
			} else {
				this.empty();
				this.isEmpty = true;
				this.selectElement(this.$refs.content);
			}
		},
		clear() {
			this.pushUndo();
			this.empty();
			// 清理图片URL，避免内存泄漏
			Object.values(this.imageList).forEach(item => {
				if (item.url && item.url.startsWith('blob:')) {
					URL.revokeObjectURL(item.url);
				}
			});
			this.imageList = [];
			this.fileList = [];
			this.undoStack = [];
			this.redoStack = [];
		},
		empty() {
			this.pushUndo();
			this.$refs.content.innerHTML = "";
			let line = this.newLine();
			let after = document.createTextNode('\u200B');
			line.appendChild(after);
			this.$nextTick(() => this.selectElement(after));
		},
		showAtBox(e) {
			this.atIng = true;
			// show之后会自动更新当前搜索的text
			// this.atSearchText = "";
			let selection = window.getSelection()
			let range = selection.getRangeAt(0)
			// 光标所在坐标
			let pos = range.getBoundingClientRect();
			this.$refs.atBox.open({
				x: pos.x,
				y: pos.y
			})
			// 记录光标所在位置
			this.updateRange();
		},
		html2Escape(strHtml) {
			return strHtml.replace(/[<>&"]/g, function (c) {
				return {
					'<': '&lt;',
					'>': '&gt;',
					'&': '&amp;',
					'"': '&quot;'
				}[c];
			});
		},
		getSendText() {
			let nodes = this.$refs.content.childNodes;
			let tempText = '';
			let atUserIds = [];
			let fullList = [];
			const each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) continue;
					if (node.nodeType === 3) {
						tempText += this.html2Escape(node.textContent);
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') continue;
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							if (tempText) {
								fullList.push({ type: 'text', content: tempText, atUserIds: atUserIds });
								tempText = '';
								atUserIds = [];
							}
							fullList.push({ type: 'image', content: this.imageList[imgId] });
						} else {
							tempText += node.dataset.emojiCode || node.alt || '';
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId;
						if (fileId) {
							if (tempText) {
								fullList.push({ type: 'text', content: tempText, atUserIds: atUserIds });
								tempText = '';
								atUserIds = [];
							}
							fullList.push({ type: 'file', content: this.fileList[fileId] });
						} else {
							// 处理粘贴的富文本内容
							if (node.className && node.className.includes('pasted-')) {
								// 处理粘贴的富文本元素
								this.processPastedElement(node, fullList, tempText, atUserIds);
								tempText = '';
								atUserIds = [];
							} else {
								tempText += '\n';
								each(node.childNodes);
							}
						}
					} else if (nodeName === 'span') {
						if (node.dataset.id) {
							tempText += this.atUserSendText(node);
							atUserIds.push(node.dataset.id);
						} else if (node.classList && node.classList.contains('emoji-flag')) {
							tempText += node.textContent || '';
						} else if (node.outerHTML) {
							// 处理粘贴的富文本span元素
							if (node.className && node.className.includes('pasted-')) {
								this.processPastedElement(node, fullList, tempText, atUserIds);
								tempText = '';
								atUserIds = [];
							} else {
								tempText += node.outerHTML;
							}
						}
					} else if (nodeName === 'a') {
						if (node.classList && node.classList.contains('emoji-flag')) {
							tempText += node.textContent || '';
						} else {
							tempText += node.innerHTML;
						}
					} else if (nodeName === 'br') {
						tempText += '\n';
					} else {
						// 处理其他HTML元素
						if (node.className && node.className.includes('pasted-')) {
							this.processPastedElement(node, fullList, tempText, atUserIds);
							tempText = '';
							atUserIds = [];
						} else {
							tempText += node.textContent || '';
						}
					}
				}
			}
			each(nodes);
			return this.normalizeSendText(tempText);
		},

		// 处理粘贴的富文本元素
		processPastedElement(element, fullList, tempText, atUserIds) {
			try {
				if (!element || !fullList) {
					console.warn('Invalid element or fullList in processPastedElement');
					return;
				}

				// 根据元素类型处理
				let tagName = element.className ? element.className.replace('pasted-', '') : '';
				let content = element.textContent || element.innerText || '';

				// 添加格式标记
				let formattedContent = this.addFormatting(content, tagName);

				fullList.push({
					type: 'text',
					content: formattedContent,
					atUserIds: Array.isArray(atUserIds) ? [...atUserIds] : [], // 创建副本避免引用问题
					format: tagName
				});
			} catch (error) {
				console.error('Error in processPastedElement:', error);
			}
		},

		// 添加格式标记
		addFormatting(content, tagName) {
			try {
				if (!content || typeof content !== 'string') {
					return '';
				}

				if (!tagName || typeof tagName !== 'string') {
					return content;
				}

				// 限制内容长度，防止过长的内容
				if (content.length > 10000) {
					content = content.substring(0, 10000) + '...';
				}

				switch (tagName) {
					case 'strong':
					case 'b':
						return `**${content}**`;
					case 'em':
					case 'i':
						return `*${content}*`;
					case 'u':
						return `__${content}__`;
					case 's':
						return `~~${content}~~`;
					case 'code':
						return `\`${content}\``;
					case 'pre':
						return `\`\`\`\n${content}\n\`\`\``;
					case 'blockquote':
						return `> ${content}`;
					case 'h1':
						return `# ${content}`;
					case 'h2':
						return `## ${content}`;
					case 'h3':
						return `### ${content}`;
					case 'h4':
						return `#### ${content}`;
					case 'h5':
						return `##### ${content}`;
					case 'h6':
						return `###### ${content}`;
					default:
						return content;
				}
			} catch (error) {
				console.error('Error in addFormatting:', error);
				return content || '';
			}
		},
		submit() {
			let nodes = this.$refs.content.childNodes;
			let fullList = [];
			let tempText = '';
			let atUserIds = [];
			let each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) {
						continue;
					}
					if (node.nodeType === 3) {
						tempText += this.html2Escape(node.textContent);
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') {
						continue;
					}
					let text = this.normalizeSendText(tempText);
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'image',
								content: this.imageList[imgId]
							})
						} else {
							tempText += node.dataset.emojiCode || node.alt || '';
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId
						// 文件
						if (fileId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'file',
								content: this.fileList[fileId]
							})
						} else {
							// 处理粘贴的富文本内容
							if (node.className && node.className.includes('pasted-')) {
								if (text) {
									fullList.push({
										type: 'text',
										content: text,
										atUserIds: atUserIds
									})
									tempText = '';
									atUserIds = []
								}
								this.processPastedElement(node, fullList, tempText, atUserIds);
								tempText = '';
								atUserIds = [];
							} else {
								tempText += '\n';
								each(node.childNodes);
							}
						}
					} else if (nodeName === 'span') {
						if (node.dataset.id) {
							tempText += this.atUserSendText(node);
							atUserIds.push(node.dataset.id)
						} else if (node.classList && node.classList.contains('emoji-flag')) {
							tempText += node.textContent || '';
						} else if (node.outerHTML) {
							// 处理粘贴的富文本span元素
							if (node.className && node.className.includes('pasted-')) {
								if (text) {
									fullList.push({
										type: 'text',
										content: text,
										atUserIds: atUserIds
									})
									tempText = '';
									atUserIds = []
								}
								this.processPastedElement(node, fullList, tempText, atUserIds);
								tempText = '';
								atUserIds = [];
							} else {
								tempText += node.outerHTML;
							}
						}
					} else if (nodeName === 'a') {
						if (node.classList && node.classList.contains('emoji-flag')) {
							tempText += node.textContent || '';
						} else {
							tempText += node.innerHTML;
						}
					} else if (nodeName === 'br') {
						tempText += '\n';
					} else {
						// 处理其他HTML元素
						if (node.className && node.className.includes('pasted-')) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							this.processPastedElement(node, fullList, tempText, atUserIds);
							tempText = '';
							atUserIds = [];
						} else {
							tempText += node.textContent || '';
						}
					}
				}
			}
			each(nodes)
			let text = this.normalizeSendText(tempText);
			if (text !== '') {
				fullList.push({
					type: 'text',
					content: text,
					atUserIds: atUserIds
				})
			}
			this.$emit('submit', fullList);
			this.updateChatStore('empty')
			// 等待dom更新
			setTimeout(() => {
				let s = this.$refs.content.innerHTML.trim();
				// 空dom时，需要刷新dom
				if (this.isEditorVisualEmpty(s)) {
					// 拼接随机长度的空格，以刷新dom
					this.empty();
					this.isEmpty = true;
					this.selectElement(this.$refs.content);
				} else {
					this.isEmpty = false;
				}
			})
		},
		focus() {
			this.$refs.content.focus();
		},
		updateChatStore(content) {
			// 清除之前的定时器
			if (this.updateTimer) {
				clearTimeout(this.updateTimer);
			}

			// 立即保存的情况
			if (content === 'empty' || content === 'immediate') {
				this._saveDraft(content);
				return;
			}

			// 防抖：延迟300ms保存
			this.updateTimer = setTimeout(() => {
				this._saveDraft(content);
			}, 300);
		},

		_saveDraft(content, chat) {
			if (!this.$refs.content) {
				return;
			}
			let s = this.$refs.content.innerHTML.trim();
			if (content === 'empty') {
				s = ''
			}
			if (this.isEditorVisualEmpty(s)) {
				s = ''
			}
			if (s && this.$emo.unwrapFlagElementsHtml) {
				s = this.$emo.unwrapFlagElementsHtml(s);
			}
			this.$store.commit("saveDraftContent", {
				content: s,
				chat: chat || this.chat
			});
		}
	},
	computed: {
		placeholderText() {
			if (this.sendMode === 'mod_enter_send') {
				return '请输入消息（按Ctrl/Cmd+Enter发送，Enter换行）';
			}
			return '请输入消息（按Enter发送，Ctrl/Cmd+Enter换行）';
		}
	},
}
</script>

<style lang="scss">
.chat-input-area {
	width: 100%;
	height: 100%;
	position: relative;

	.edit-chat-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		outline: none;
		padding: 5px;
		line-height: 26px;
		font-size: var(--im-font-size);
		text-align: left;
		overflow-y: auto;

		a {
			color: inherit;
			text-decoration: none;
		}

		.emoji-flag {
			cursor: text;
		}

		// 单独一行时，无法在前面输入的bug
		>div:before {
			content: "\200B";
			font-size: 14px;
			position: absolute;
			top: 0;
			left: 0;
		}

		.chat-image {
			display: block;
			max-width: 200px;
			max-height: 100px;
			border: 1px solid #e6e6e6;
			cursor: pointer;
		}

		.chat-file-container {
			max-width: 65%;
			padding: 10px;
			border: 2px solid #587ff0;
			display: flex;
			background: #eeeC;
			border-radius: 10px;

			.file-position-left {
				display: flex;
				width: 80px;
				justify-content: center;
				align-items: center;

				.el-icon-document {
					font-size: 40px;
					text-align: center;
					color: #d42e07;
				}
			}

			.file-position-right {
				flex: 1;

				.file-name {
					font-size: 16px;
					font-weight: 600;
					color: #66b1ff;
				}

				.file-size {
					font-size: 14px;
					font-weight: 600;
				}
			}
		}

		.chat-at-user {
			color: #00f;
			border-radius: 3px;
			user-select: all;
			-webkit-user-select: all;
		}

		// 粘贴的富文本样式
		.pasted-div {
			margin: 5px 0;
			padding: 5px;
			border-left: 3px solid #d52324;
			background-color: rgba(213, 35, 36, 0.05);
		}

		.pasted-p {
			margin: 5px 0;
			line-height: 1.6;
		}

		.pasted-h1,
		.pasted-h2,
		.pasted-h3,
		.pasted-h4,
		.pasted-h5,
		.pasted-h6 {
			margin: 10px 0 5px 0;
			font-weight: bold;
			color: #d52324;
		}

		.pasted-h1 {
			font-size: 20px;
		}

		.pasted-h2 {
			font-size: 18px;
		}

		.pasted-h3 {
			font-size: 16px;
		}

		.pasted-h4 {
			font-size: 15px;
		}

		.pasted-h5 {
			font-size: 14px;
		}

		.pasted-h6 {
			font-size: 13px;
		}

		.pasted-strong {
			font-weight: bold;
			color: #d52324;
		}

		.pasted-em {
			font-style: italic;
		}

		.pasted-u {
			text-decoration: underline;
		}

		.pasted-s {
			text-decoration: line-through;
		}

		.pasted-code {
			background-color: #f5f5f5;
			padding: 2px 4px;
			border-radius: 3px;
			font-family: 'Courier New', monospace;
			font-size: 12px;
			color: #d52324;
		}

		.pasted-pre {
			background-color: #f8f8f8;
			padding: 10px;
			border-radius: 5px;
			font-family: 'Courier New', monospace;
			font-size: 12px;
			white-space: pre-wrap;
			border: 1px solid #e0e0e0;
			margin: 5px 0;
		}

		.pasted-blockquote {
			margin: 10px 0;
			padding: 10px 15px;
			border-left: 4px solid #d52324;
			background-color: rgba(213, 35, 36, 0.05);
			font-style: italic;
		}

		.pasted-ul,
		.pasted-ol {
			margin: 10px 0;
			padding-left: 20px;
		}

		.pasted-li {
			margin: 3px 0;
		}

		.pasted-a {
			color: #d52324;
			text-decoration: underline;
		}

	}

	.edit-chat-container:after {
		content: attr(data-placeholder);
		color: gray;
		position: absolute;
		left: 6px;
		top: 5px;
		pointer-events: none;
	}

	.edit-chat-container.not-empty:after {
		content: none;
	}

}
</style>
